'use client';

/**
 * ONE shared way to react to element resizes without feeding the browser's ResizeObserver loop.
 *
 * A ResizeObserver callback that writes state which changes the observed element's size is delivered again in the same
 * frame; when it keeps going the browser drops it ("ResizeObserver loop completed with undelivered notifications") and
 * React can run into "Maximum update depth exceeded" (#185). Here the observer callback only schedules a frame; the
 * frame callback runs ONCE per animation frame however many notifications arrived, so it can read everything it needs
 * and then write once. A write made in a frame is seen by the observer in the NEXT frame, never in the same delivery.
 *
 * Use `observeResizeRaf` inside an existing effect, or `useRafResizeObserver` when the effect is only the observer.
 */

import { useEffect, useRef, type DependencyList } from 'react';

type Observable = Element | null | undefined;

/** Observe `nodes`; `onFrame` runs at most once per animation frame. Returns the disposer (disconnects, cancels the pending frame). */
export function observeResizeRaf(nodes: readonly Observable[], onFrame: () => void): () => void {
  if (typeof ResizeObserver === 'undefined') return () => {};
  let frame = 0;
  const run = () => {
    frame = 0;
    onFrame();
  };
  const observer = new ResizeObserver(() => {
    if (!frame) frame = requestAnimationFrame(run);
  });
  for (const node of nodes) if (node) observer.observe(node);
  return () => {
    observer.disconnect();
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
  };
}

/** `observeResizeRaf` for a component: `getNodes` is read when `deps` change, and the latest `onFrame` always runs. */
export function useRafResizeObserver(getNodes: () => readonly Observable[], onFrame: () => void, deps: DependencyList): void {
  const onFrameRef = useRef(onFrame);
  onFrameRef.current = onFrame;
  useEffect(
    () => observeResizeRaf(getNodes(), () => onFrameRef.current()),
    // `getNodes` is read per `deps`, by contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps,
  );
}
